import axios from "axios";
import { BASE_URL, TIME_OUT } from "@/config/env";
import type {
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosInstance,
  AxiosError
} from "axios";
import { message } from "ant-design-vue";
import router from "@/router";
import useAccount from "@/store/modules/account";

message.config({
  maxCount: 1
});

// import cache from "@/utils/cache";
export interface IResponseData<T = any> {
  code: number;
  msg: string;
  data: T;
}
type requestMethods = (
  url: string,
  params?: any,
  config?: AxiosRequestConfig
) => Promise<IResponseData<any>>;

interface IService {
  get: requestMethods;
  post: requestMethods;
  put: requestMethods;
  delete: requestMethods;
  download: requestMethods;
  /* http: AxiosInstance;
  addInterceptors: (instance: AxiosInstance) => void;
  request: (p: AxiosPromise) => Promise<IResponseData<any>>; */
}

class HttpService implements IService {
  private http: AxiosInstance;

  constructor(config: AxiosRequestConfig) {
    this.http = axios.create(config);
    this.addInterceptors(this.http);
  }

  get<T>(url: string, config?: AxiosRequestConfig) {
    return this.request<T>(this.http.get(url, config));
  }

  post<T>(url: string, params?: unknown, config?: AxiosRequestConfig) {
    return this.request<T>(this.http.post(url, params, config));
  }

  put<T>(url: string, params?: unknown, config?: AxiosRequestConfig) {
    return this.request<T>(this.http.put(url, params, config));
  }

  delete<T>(url: string, params?: unknown, config?: AxiosRequestConfig) {
    return this.request<T>(this.http.delete(url, { data: params, ...config }));
  }

  download<T>(url: string, params?: unknown) {
    return this.request<T>(
      this.http.post(url, params, {
        responseType: "arraybuffer",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        }
      })
    );
  }

  private addInterceptors(axios: AxiosInstance) {
    // 请求拦截
    axios.interceptors.request.use((config) => {
      // 请求带上token
      const accountStore = useAccount();
      if (accountStore.token && config.headers) {
        config.headers["Auth"] = `Bearer ${accountStore.token}`;
        /* config.headers[
          "Auth"
        ] = `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX25hbWUiOiJlcmljLnRhbmciLCJyZWFsX25hbWUiOiLllJDlvawiLCJtdWx0aXBsZV9pZCI6IjE0MyIsImF1dGhvcml0aWVzIjpbImFkbWluIl0sImNsaWVudF9pZCI6ImJvcCIsInJvbGVfbmFtZSI6ImFkbWluIiwidXNlcl9pZCI6IjE0MyIsInJvbGVfaWQiOiIzIiwic2NvcGUiOlsiYWxsIl0sImRldGFpbCI6bnVsbCwiZXhwIjoxNjgyMTI5MTc4LCJkZXB0X2lkIjoiMTAwMDAwMDAwMDAwMDAwMDAwMiIsImp0aSI6IjI4NDZlOGEwLWYyZmItNDFhNi04ZmZkLTViYzM5NGY4ODJkZiIsImFjY291bnQiOiJlcmljLnRhbmcifQ.zw4DJYcj2YVNB_2n7Lk-wMTZ1sJur0tSDV1nuPkd45Y`; */
      }
      if (config.method === "get" && config.responseType === "blob") {
        config.data = true;
        config.headers!["Content-Type"] = "application/x-www-form-urlencoded";
      }
      return config;
    });

    // 响应拦截
    axios.interceptors.response.use(
      (response: AxiosResponse<IResponseData>) => {
        switch (response.data.code) {
          case 500:
            message.error(response.data.msg);
            break;
          case 400:
            message.warn(response.data.msg);
            break;
        }
        return response;
      },
      (error: AxiosError<IResponseData>) => {
        console.warn("network error", error);
        const res = error.response?.data;
        const throwKey = "Auto-Throw-Error";
        const isNotAutoThrow = error.config.headers![throwKey] === false;
        // 有响应的情况, 抛出接口的msg
        if (res) {
          switch (res.code) {
            case 401:
              message
                .error("用户信息过期, 请重新登录！")
                .then(() => router.push("/login"));
              /* // 新系统暂无登录接口 所以暂时跳转旧系统测试环境
                .then(() => {
                  let href = "";
                  const account = useAccount();
                  // 跳生产环境
                  if (account.isProduct) {
                    href = "https://portal.tec-do.com/user/login.jsp";
                  } else {
                    // 测试环境或开发环境  跳测试环境
                    href = "http://172.24.2.38:8888/user/login.jsp";
                  }
                  cache.clearAllCache();
                  window.open(href, "_self");
                }); */
              break;
            case 403:
              !isNotAutoThrow && message.warn(res.msg);
              break;
            case 500:
              !isNotAutoThrow && message.error(res.msg);
              break;
            case 400:
              !isNotAutoThrow && message.warn(res.msg);
              break;
            default:
              res.msg && !isNotAutoThrow && message.error(res.msg);
          }
        } else {
          // 没有响应的情况下, 报网络错误
          message.error("网络错误, 请稍后再试!");
        }
        return Promise.reject(error);
      }
    );
  }

  private async request<T>(p: AxiosPromise): Promise<IResponseData<T>> {
    return p
      .then((response) => {
        if (response.headers["content-type"].includes("application/json")) {
          return response.data;
        }
        return response;
      })
      .catch((error: AxiosError) => {
        // message.error("网络错误,请稍后重试!");
        return error.response?.data;
      });
  }
}

export default new HttpService({
  baseURL: BASE_URL,
  timeout: TIME_OUT
});
